// pages/Audit.jsx — Audit engagements (Audit Phase 0, Oct 2026).
// Credentials, requirement check, prepaid credits, and the audit engagement workspace.
// This page only presents and carries requests; every rule is enforced by the server.
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getClients, getAuditOverview, listAuditEngagements } from '../api/kailux'
import BackButton from '../components/BackButton'
import Spinner from '../components/Spinner'
import CredentialsPanel from '../components/audit/CredentialsPanel'
import CreditsPanel from '../components/audit/CreditsPanel'
import AssurancePanel from '../components/audit/AssurancePanel'
import EngagementWorkspace from '../components/audit/EngagementWorkspace'
import { Card, Notice, Pill, toneForStatus } from '../components/audit/ui'
import { STATUS_LABEL, basisLabel, auditErrorText, formatDate } from '../utils/audit'
import { T } from '../theme/theme'

const TABS = [
  { key: 'engagements', label: 'Engagements' },
  { key: 'check', label: 'Requirement check' },
  { key: 'credits', label: 'Credits' },
  { key: 'credentials', label: 'Auditor credentials' },
]

export default function Audit() {
  const { firm } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState('engagements')
  const [overview, setOverview] = useState(null)
  const [clients, setClients] = useState([])
  const [engagements, setEngagements] = useState([])
  const [openId, setOpenId] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!firm?.id) return
    try {
      const [o, c, e] = await Promise.all([getAuditOverview(), getClients(firm.id), listAuditEngagements()])
      setOverview(o); setClients(c.data || []); setEngagements(e.engagements || [])
      setError('')
    } catch (err) {
      setError(auditErrorText(err, 'Could not load the audit area.'))
    } finally { setLoading(false) }
  }, [firm?.id])
  useEffect(() => { load() }, [load])

  const nameOf = (id) => clients.find((c) => c.id === id)?.name || 'Client'
  const open = engagements.find((e) => e.id === openId)

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: "'Outfit',sans-serif", color: T.tx, padding: '32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
        <BackButton onClick={() => navigate('/dashboard')} label="Dashboard" />
        <div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '22px', fontWeight: 800 }}>Audit</div>
          <div style={{ fontSize: '13px', color: T.mt, marginTop: '4px' }}>
            KaiLux enforces who may sign and keeps the file's record. The Registered Auditor forms and signs the opinion.
          </div>
        </div>
      </div>

      {error && <Notice tone="bad" testId="audit-error">{error}</Notice>}
      {loading ? <Spinner /> : (
        <>
          {overview && !overview.report_issuance_enabled && (
            <Notice tone="info" testId="issuance-off">
              Issuing audit reports is not switched on yet. You can set up credentials, run the requirement check and prepare the audit file.
            </Notice>
          )}
          <div role="tablist" style={{ display: 'flex', gap: '6px', marginBottom: '18px', flexWrap: 'wrap' }}>
            {TABS.map((t) => (
              <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => { setTab(t.key); setOpenId(null) }}
                style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                  border: `1px solid ${tab === t.key ? T.or : T.bd}`, background: tab === t.key ? T.ord : 'transparent',
                  color: tab === t.key ? T.or : T.mt, fontFamily: "'Outfit',sans-serif" }}>{t.label}</button>
            ))}
          </div>

          {tab === 'engagements' && (open
            ? <EngagementWorkspace engagementId={open.id} firmId={firm.id} clientName={nameOf(open.client_id)} onBack={() => { setOpenId(null); load() }} />
            : (
              <Card testId="engagement-list-card" title="Audit engagements">
                {engagements.length === 0 && <div style={{ fontSize: '13px', color: T.mt }}>No audit engagements yet. Run the requirement check to open one.</div>}
                {engagements.map((e) => (
                  <button key={e.id} type="button" data-testid={`engagement-${e.id}`} onClick={() => setOpenId(e.id)}
                    style={{ display: 'flex', width: '100%', gap: '12px', alignItems: 'center', textAlign: 'left', padding: '12px 4px',
                      background: 'transparent', border: 'none', borderBottom: `1px solid ${T.bd}`, cursor: 'pointer', color: T.tx, fontFamily: "'Outfit',sans-serif" }}>
                    <span style={{ flex: 1, fontSize: '14px', fontWeight: 700 }}>{nameOf(e.client_id)} — {e.financial_year}</span>
                    <span style={{ fontSize: '12px', color: T.mt }}>{basisLabel(e.engagement_basis)}</span>
                    {e.report_date && <span style={{ fontSize: '12px', color: T.mt }}>Report {formatDate(e.report_date)}</span>}
                    <Pill tone={toneForStatus(e.status)}>{STATUS_LABEL[e.status]}</Pill>
                  </button>
                ))}
              </Card>
            ))}
          {tab === 'check' && <AssurancePanel clients={clients} onEngagementCreated={(eng) => { load().then(() => { setTab('engagements'); setOpenId(eng.id) }) }} />}
          {tab === 'credits' && <CreditsPanel isAdminOfFirm={firm?.firm_role === 'admin'} onChanged={load} />}
          {tab === 'credentials' && <CredentialsPanel overview={overview} onChanged={load} />}
        </>
      )}
    </div>
  )
}
